import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AuditListParams, AuditStatus } from '../types/audit';

export type AuditTab = 'dashboard' | 'list';

export interface AuditUrlState {
  tab: AuditTab;
  filters: AuditListParams;
  page: number;
  pageSize: number;
}

const DEFAULT_PAGE_SIZE = 50;

const STRING_FILTER_KEYS = [
  'resource_type',
  'resource_id',
  'action',
  'actor_username',
  'from_timestamp',
  'to_timestamp',
] as const;

function parseStatus(v: string | null): AuditStatus | undefined {
  return v === 'success' || v === 'failed' || v === 'denied' ? v : undefined;
}

function parseTab(v: string | null): AuditTab {
  return v === 'list' ? 'list' : 'dashboard';
}

function parsePosInt(v: string | null, def: number): number {
  if (!v) return def;
  const n = parseInt(v, 10);
  return Number.isFinite(n) && n > 0 ? n : def;
}

function parseFromParams(p: URLSearchParams): AuditUrlState {
  const filters: AuditListParams = {};
  for (const k of STRING_FILTER_KEYS) {
    const v = p.get(k);
    if (v) (filters as Record<string, string>)[k] = v;
  }
  const st = parseStatus(p.get('status'));
  if (st) filters.status = st;
  if (p.get('exclude_reads') === '1') filters.exclude_reads = true;
  // authenticated_only defaults to TRUE when missing from the URL so the
  // Events tab focuses on real users like the Dashboard does. The URL only
  // serialises the explicit "off" state (clean URLs for the common case).
  const auth = p.get('authenticated_only');
  filters.authenticated_only = auth === '0' ? false : true;

  return {
    tab: parseTab(p.get('tab')),
    filters,
    page: parsePosInt(p.get('page'), 1),
    pageSize: parsePosInt(p.get('page_size'), DEFAULT_PAGE_SIZE),
  };
}

function serializeToParams(s: AuditUrlState): URLSearchParams {
  const np = new URLSearchParams();
  if (s.tab !== 'dashboard') np.set('tab', s.tab);
  if (s.page !== 1) np.set('page', String(s.page));
  if (s.pageSize !== DEFAULT_PAGE_SIZE) np.set('page_size', String(s.pageSize));
  for (const k of STRING_FILTER_KEYS) {
    const v = (s.filters as Record<string, string | undefined>)[k];
    if (v) np.set(k, v);
  }
  if (s.filters.status) np.set('status', s.filters.status);
  if (s.filters.exclude_reads) np.set('exclude_reads', '1');
  if (s.filters.authenticated_only === false) np.set('authenticated_only', '0');
  return np;
}

export interface UseAuditUrlState {
  state: AuditUrlState;
  setTab: (tab: AuditTab) => void;
  setFilters: (filters: AuditListParams) => void;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  patch: (next: Partial<AuditUrlState>) => void;
}

/**
 * Audit log URL state: filters, page, page size, and active tab live in
 * the query string so the view is shareable and back/forward-friendly.
 * Defaults are not serialised (clean URLs for the common case).
 */
export function useAuditUrlState(): UseAuditUrlState {
  const [params, setParams] = useSearchParams();

  const state = useMemo(() => parseFromParams(params), [params]);

  const patch = useCallback(
    (next: Partial<AuditUrlState>) => {
      setParams(
        (prev) => {
          const cur = parseFromParams(prev);
          return serializeToParams({ ...cur, ...next });
        },
        { replace: true },
      );
    },
    [setParams],
  );

  return {
    state,
    setTab: useCallback((tab: AuditTab) => patch({ tab }), [patch]),
    // Filter changes always reset to page 1 — otherwise users land on
    // an empty page of a now-shorter result set.
    setFilters: useCallback(
      (filters: AuditListParams) => patch({ filters, page: 1 }),
      [patch],
    ),
    setPage: useCallback((page: number) => patch({ page }), [patch]),
    setPageSize: useCallback(
      (pageSize: number) => patch({ pageSize, page: 1 }),
      [patch],
    ),
    patch,
  };
}
